import { InfoCircleOutlined } from '@ant-design/icons';
import { useMemo } from 'react';
import { Button, Divider, Dropdown, Switch, Tooltip } from 'antd';
import HeaderSortDescChevron from 'assets/svg/HeaderSortDescChevron';
import AxisConfigDropdown from 'components/molecules/PackArchitecture/AxisConfigDropdown';
import type { AxisConfiguration } from 'components/molecules/PackArchitecture/AxisConfigurationModal';
import type { KpiAxisConfiguration } from 'components/molecules/PackArchitecture/KpiAxisConfigurationModal';
import type { AxisGroupByControlsMeta } from 'components/molecules/PackArchitecture/axisGroupByControlsMeta.types';
import type { KpiAxisControlsMeta } from 'components/molecules/PackArchitecture/kpiAxisControlsMeta.types';
import type { KpiBinLabelAffixes, KpiDataRange } from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import WhitespaceValueSalesLegend from 'components/organism/WhitespaceAnalysis/WhitespaceValueSalesLegend';
import type { WhitespaceValueSalesLegendData } from 'components/pages/WhitespaceAnalysis/whitespaceGridKpiAxis.helpers';
import {
    PPA_WHITESPACE_AXIS_X,
    PPA_WHITESPACE_AXIS_X_SETTINGS,
    PPA_WHITESPACE_AXIS_Y,
    PPA_WHITESPACE_AXIS_Y_SETTINGS,
    PPA_WHITESPACE_CROSS_SECTION_BTN,
    PPA_WHITESPACE_SHOW_TOGGLE,
    PPA_WHITESPACE_TOOLBAR,
    ppaWhitespaceCrossSectionOptionTestId,
} from 'constants/ppaTestIds';
import {
    buildWhitespaceXAxisOptionGroups,
    buildWhitespaceYAxisOptionGroups,
} from 'components/pages/WhitespaceAnalysis/whitespaceMeta.helpers';
import type { WhitespaceOptionItem } from 'contexts/whitespaceAnalysisContext.types';
import { colorConstants } from 'config/colorConstants';

const FILTER_BUTTON_CLASS =
    '!h-8 !rounded-md !border-borderGreyAl1 !bg-white !px-3 !text-[14px] !font-normal !text-blackTitle !flex !justify-center !items-center hover:!border-purpleFontSelected active:!border-purpleFontPressed';

const WHITESPACE_DEFINITION_TOOLTIP_OVERLAY_STYLE = {
    width: 'auto',
    maxWidth: '550px',
    padding: '6px 8px',
    borderRadius: '6px',
} as const;

const splitWhitespaceTooltipLines = (text: string) =>
    text
        .replace(/\\n/g, '\n')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);

const WhitespaceDefinitionTooltipContent = ({ text }: { text: string }) => {
    const lines = splitWhitespaceTooltipLines(text);
    if (lines.length === 0) return null;

    const [header, ...bulletLines] = lines;

    return (
        <div>
            <p className="m-0 mb-[5px] text-[14px] font-bold leading-[22px] text-white">{header}</p>
            {bulletLines.length > 0 ? (
                <ul className="m-0 list-disc pl-[18px] text-[14px] font-normal leading-[22px] text-white">
                    {bulletLines.map((line, index) => (
                        <li key={`${line}-${index}`}>{line}</li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
};

type WhitespaceAnalysisToolbarProps = {
    xAxis: string;
    yAxis: string;
    xAxisOptionItems: WhitespaceOptionItem[];
    yAxisOptionItems: WhitespaceOptionItem[];
    yAxisKpiOptionIds: string[];
    crossSection: string;
    crossSectionOptions: string[];
    showWhitespaces: boolean;
    isCrossSectionOpen: boolean;
    totalColumnCount: number;
    /** Bucket count for the current Y-axis (attribute buckets, e.g. Pack_Range, or numeric bins) - seeds the settings modal before its own live preview loads. */
    totalRowCount: number;
    gridRequestPayload: Record<string, unknown>;
    xAxisDimensionConfig?: AxisConfiguration;
    yAxisKpiConfig?: KpiAxisConfiguration;
    /** Y-axis attribute (e.g. Pack_Range) dimension config - only relevant when isYAxisAttribute is true. */
    yAxisDimensionConfig?: AxisConfiguration;
    /** True when the selected Y option is a config-driven attribute (e.g. Pack_Range) rather than a KPI. */
    isYAxisAttribute: boolean;
    xAxisGroupByControlsMeta: AxisGroupByControlsMeta | null;
    yAxisGroupByControlsMeta: AxisGroupByControlsMeta | null;
    yAxisKpiControlsMeta: KpiAxisControlsMeta | null;
    yAxisKpiDataRange: KpiDataRange;
    yAxisKpiBinLabelAffixes: KpiBinLabelAffixes;
    whitespaceDefinitionTooltip?: string;
    valueSalesLegend: WhitespaceValueSalesLegendData | null;
    isLoading: boolean;
    onAxisMenuOpenChange: (open: boolean) => void;
    onXAxisChange: (value: string) => void;
    onYAxisChange: (value: string) => void;
    onXAxisConfigurationChange: (config: AxisConfiguration) => void;
    onYAxisKpiConfigurationChange: (config: KpiAxisConfiguration) => void;
    onYAxisDimensionConfigurationChange: (config: AxisConfiguration) => void;
    onCrossSectionOpenChange: (open: boolean) => void;
    onCrossSectionSelect: (option: string) => void;
    onShowWhitespacesChange: (checked: boolean) => void;
};

const WhitespaceAnalysisToolbar = ({
    xAxis,
    yAxis,
    xAxisOptionItems,
    yAxisOptionItems,
    yAxisKpiOptionIds,
    crossSection,
    crossSectionOptions,
    showWhitespaces,
    isCrossSectionOpen,
    totalColumnCount,
    totalRowCount,
    gridRequestPayload,
    xAxisDimensionConfig,
    yAxisKpiConfig,
    yAxisDimensionConfig,
    isYAxisAttribute,
    xAxisGroupByControlsMeta,
    yAxisGroupByControlsMeta,
    yAxisKpiControlsMeta,
    yAxisKpiDataRange,
    yAxisKpiBinLabelAffixes,
    whitespaceDefinitionTooltip,
    valueSalesLegend,
    isLoading,
    onAxisMenuOpenChange,
    onXAxisChange,
    onYAxisChange,
    onXAxisConfigurationChange,
    onYAxisKpiConfigurationChange,
    onYAxisDimensionConfigurationChange,
    onCrossSectionOpenChange,
    onCrossSectionSelect,
    onShowWhitespacesChange,
}: WhitespaceAnalysisToolbarProps) => {
    const xAxisOptionGroups = useMemo(
        () => buildWhitespaceXAxisOptionGroups(xAxisOptionItems),
        [xAxisOptionItems],
    );
    const yAxisOptionGroups = useMemo(
        () => buildWhitespaceYAxisOptionGroups(yAxisOptionItems),
        [yAxisOptionItems],
    );

    const crossSectionDropdownContent = (
        <div className="w-[160px] rounded-lg bg-white p-1 shadow-[0px_3px_6px_-4px_rgba(0,0,0,0.12),0px_9px_28px_8px_rgba(0,0,0,0.05)]">
            {crossSectionOptions.map((option) => {
                const isSelected = crossSection === option;
                return (
                    <button
                        key={option}
                        type="button"
                        aria-pressed={isSelected}
                        className={`flex h-8 w-full items-center rounded px-3 text-left text-[14px] leading-[22px] ${isSelected ? 'font-semibold text-purpleFont' : 'font-normal text-blackTitle'}`}
                        style={isSelected ? { backgroundColor: colorConstants.axisSelectedBg } : undefined}
                        data-testid={ppaWhitespaceCrossSectionOptionTestId(option)}
                        onClick={() => onCrossSectionSelect(option)}
                    >
                        {option}
                    </button>
                );
            })}
        </div>
    );

    return (
        <div className="flex items-center gap-[20px]" data-testid={PPA_WHITESPACE_TOOLBAR}>
            <div className="flex flex-1 items-center gap-3">
                <div className="flex items-center gap-3">
                    <AxisConfigDropdown
                        label="X Axis"
                        value={xAxis}
                        onChange={onXAxisChange}
                        optionGroups={xAxisOptionGroups}
                        showSettings={Boolean(xAxisGroupByControlsMeta)}
                        hideGroupRestAsOther
                        useWhitespacePreview
                        whitespacePreviewRequest={gridRequestPayload}
                        groupByControlsMeta={xAxisGroupByControlsMeta}
                        axisConfiguration={xAxisDimensionConfig}
                        onAxisConfigurationChange={onXAxisConfigurationChange}
                        apiTotalCount={totalColumnCount}
                        onOpenChange={onAxisMenuOpenChange}
                        triggerTestId={PPA_WHITESPACE_AXIS_X}
                        settingsTestId={PPA_WHITESPACE_AXIS_X_SETTINGS}
                        isLoading={isLoading}
                    />
                    <AxisConfigDropdown
                        label="Y Axis"
                        value={yAxis}
                        onChange={onYAxisChange}
                        optionGroups={yAxisOptionGroups}
                        kpiColumnOptionIds={yAxisKpiOptionIds}
                        showSettings={Boolean(yAxisKpiControlsMeta) || Boolean(yAxisGroupByControlsMeta)}
                        hideGroupRestAsOther
                        useWhitespacePreview
                        whitespacePreviewRequest={gridRequestPayload}
                        whitespacePreviewAxisKind={isYAxisAttribute ? 'row' : 'column'}
                        kpiControlsMeta={yAxisKpiControlsMeta}
                        kpiAxisConfiguration={yAxisKpiConfig}
                        onKpiAxisConfigurationChange={onYAxisKpiConfigurationChange}
                        kpiDataRange={yAxisKpiDataRange}
                        kpiBinLabelAffixes={yAxisKpiBinLabelAffixes}
                        groupByControlsMeta={yAxisGroupByControlsMeta}
                        axisConfiguration={yAxisDimensionConfig}
                        onAxisConfigurationChange={onYAxisDimensionConfigurationChange}
                        apiTotalCount={totalRowCount}
                        onOpenChange={onAxisMenuOpenChange}
                        triggerTestId={PPA_WHITESPACE_AXIS_Y}
                        settingsTestId={PPA_WHITESPACE_AXIS_Y_SETTINGS}
                        isLoading={isLoading}
                    />
                </div>
                <Dropdown
                    trigger={['click']}
                    placement="bottomLeft"
                    open={isCrossSectionOpen}
                    onOpenChange={onCrossSectionOpenChange}
                    dropdownRender={() => crossSectionDropdownContent}
                >
                    <Button
                        className={`${FILTER_BUTTON_CLASS} ${isCrossSectionOpen ? '!border-purpleFontPressed' : ''}`}
                        data-testid={PPA_WHITESPACE_CROSS_SECTION_BTN}
                    >
                        <span className="text-fontGray mr-1">Cross Section:</span>
                        <span className="text-fontGray mr-2">{crossSection}</span>
                        <HeaderSortDescChevron color={colorConstants.blackTitle} />
                    </Button>
                </Dropdown>
            </div>
            <div className="flex items-center gap-[20px]">
                {valueSalesLegend ? <WhitespaceValueSalesLegend {...valueSalesLegend} /> : null}
                <Divider type="vertical" className="!mx-0 self-center" />
                <div className="flex items-center gap-2">
                    <Switch
                        size="small"
                        checked={showWhitespaces}
                        onChange={onShowWhitespacesChange}
                        data-testid={PPA_WHITESPACE_SHOW_TOGGLE}
                    />
                    <div className="flex cursor-pointer items-center gap-2">
                        <span className="text-[14px] leading-[22px] text-blackTitle">Show Whitespaces</span>
                        {whitespaceDefinitionTooltip ? (
                            <Tooltip
                                placement="topRight"
                                arrowPointAtCenter
                                color={colorConstants.blackTransparent88}
                                overlayInnerStyle={WHITESPACE_DEFINITION_TOOLTIP_OVERLAY_STYLE}
                                title={<WhitespaceDefinitionTooltipContent text={whitespaceDefinitionTooltip} />}
                            >
                                <InfoCircleOutlined className="ml-2 text-[14px] opacity-50" />
                            </Tooltip>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WhitespaceAnalysisToolbar;
